import React, { useState, useMemo } from 'react';

export interface SortableColumn {
  key: string;
  label: string | React.ReactNode;
  sortable?: boolean;
  render?: (value: any, row: any) => React.ReactNode;
  className?: string;
}

export interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

interface SortableTableProps {
  data: any[];
  columns: SortableColumn[];
  onRowSelect?: (selectedRows: any[]) => void;
  selectedRows?: any[];
  rowKey: string;
  className?: string;
  emptyMessage?: string;
  loading?: boolean;
  rowStyle?: (row: any) => React.CSSProperties;
}

const SortableTable: React.FC<SortableTableProps> = ({
  data,
  columns,
  onRowSelect,
  selectedRows = [],
  rowKey,
  className = '',
  emptyMessage = 'No data available',
  loading = false,
  rowStyle
}) => {
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  // Helper to detect date-like strings (ISO or common formats)
  const isDateLikeString = (v: any) =>
    typeof v === 'string' && (v.includes('-') || v.includes('/') || v.includes('T')) && !isNaN(Date.parse(v));

  function getNestedValue(obj: any, path: string) {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  const sortedData = useMemo(() => {
    if (!sortConfig) return data;

    return [...data].sort((a, b) => {
      const aValue = getNestedValue(a, sortConfig.key);
      const bValue = getNestedValue(b, sortConfig.key);

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Handle booleans
      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        const comparison = aValue === bValue ? 0 : aValue ? 1 : -1;
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      // Handle numbers
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        const comparison = aValue - bValue;
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      // Handle Date objects
      if (aValue instanceof Date && bValue instanceof Date) {
        const comparison = aValue.getTime() - bValue.getTime();
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      // Handle date-like strings BEFORE generic string comparison
      if (isDateLikeString(aValue) && isDateLikeString(bValue)) {
        const aDate = new Date(aValue);
        const bDate = new Date(bValue);
        const comparison = aDate.getTime() - bDate.getTime();
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      // Handle strings (generic)
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      // Fallback to string comparison for any other types
      const comparison = String(aValue).toLowerCase().localeCompare(String(bValue).toLowerCase());
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [data, sortConfig]);

  // getNestedValue moved above useMemo to avoid temporal dead zone during render

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleSelectAll = (checked: boolean) => {
    if (!onRowSelect) return;
    
    if (checked) {
      onRowSelect(sortedData);
    } else {
      onRowSelect([]);
    }
  };

  const handleRowSelect = (row: any, checked: boolean) => {
    if (!onRowSelect) return;

    const rowId = row[rowKey];
    if (checked) {
      onRowSelect([...selectedRows, row]);
    } else {
      onRowSelect(selectedRows.filter(r => r[rowKey] !== rowId));
    }
  };

  const isRowSelected = (row: any) => {
    const rowId = row[rowKey];
    return selectedRows.some(r => r[rowKey] === rowId);
  };

  const getSortIcon = (columnKey: string) => {
    if (!sortConfig || sortConfig.key !== columnKey) {
      return <span className="sort-icon">↕️</span>;
    }
    return sortConfig.direction === 'asc' ? 
      <span className="sort-icon">↑</span> : 
      <span className="sort-icon">↓</span>;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className={`table-container ${className}`}>
      <table className="table">
        <thead>
          <tr>
            {onRowSelect && (
              <th>
                <input
                  type="checkbox"
                  checked={selectedRows.length === sortedData.length && sortedData.length > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>
            )}
            {columns.map((column) => (
              <th 
                key={column.key} 
                className={`${column.className || ''} ${column.sortable !== false ? 'sortable' : ''}`}
                onClick={() => column.sortable !== false && handleSort(column.key)}
                style={{ cursor: column.sortable !== false ? 'pointer' : 'default' }}
              >
                <div className="th-content">
                  {column.label}
                  {column.sortable !== false && getSortIcon(column.key)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.length === 0 ? (
            <tr>
              <td colSpan={columns.length + (onRowSelect ? 1 : 0)} style={{ textAlign: 'center', padding: '40px' }}>
                <h3 style={{ color: '#7f8c8d', marginBottom: '10px' }}>No Data</h3>
                <p style={{ color: '#7f8c8d', marginBottom: '20px' }}>
                  {emptyMessage}
                </p>
              </td>
            </tr>
          ) : (
            sortedData.map((row) => (
              <tr 
                key={row[rowKey]} 
                className={isRowSelected(row) ? 'selected' : ''}
                style={rowStyle ? rowStyle(row) : undefined}
              >
                {onRowSelect && (
                  <td>
                    <input
                      type="checkbox"
                      checked={isRowSelected(row)}
                      onChange={(e) => handleRowSelect(row, e.target.checked)}
                    />
                  </td>
                )}
                {columns.map((column) => (
                  <td key={column.key} className={column.className || ''}>
                    {column.render 
                      ? column.render(getNestedValue(row, column.key), row)
                      : getNestedValue(row, column.key)
                    }
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default SortableTable;